import React, { useState } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import { NavigationTab } from '@/lib/constants';
import { ConnectionStatus } from '@/components/common/ConnectionIndicator';
import { PwaInstallBanner } from '@/components/pwa/PwaInstallBanner';
import { Menu, X } from 'lucide-react';

interface AppLayoutProps {
  children: React.ReactNode;
  activeTab: NavigationTab;
  onSelectTab: (tab: NavigationTab) => void;
  wsStatus: ConnectionStatus;
  wsReconnectAttempt?: number;
  marketStatus?: string | null;
  botStatus?: string | null;
  paperMode?: boolean;
  lastCycle?: string | null;
  lastSignal?: string | null;
  openPositionsCount?: number;
  unacknowledgedSignalsCount?: number;
  lastSyncTime?: string | number | null;
  uptime?: string | number | null;
  isMockMode?: boolean;
  onToggleMockMode?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  activeTab,
  onSelectTab,
  wsStatus,
  wsReconnectAttempt,
  marketStatus,
  botStatus,
  paperMode,
  lastCycle,
  lastSignal,
  openPositionsCount,
  unacknowledgedSignalsCount,
  lastSyncTime,
  uptime,
  isMockMode,
  onToggleMockMode,
  onRefresh,
  isRefreshing,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSelectTab = (tab: NavigationTab) => {
    onSelectTab(tab);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-app)] text-[var(--text-primary)] font-sans selection:bg-emerald-500/20 selection:text-emerald-300">
      {/* Top Header */}
      <Header
        wsStatus={wsStatus}
        wsReconnectAttempt={wsReconnectAttempt}
        marketStatus={marketStatus}
        botStatus={botStatus}
        paperMode={paperMode}
        lastCycle={lastCycle}
        lastSignal={lastSignal}
        isMockMode={isMockMode}
        onToggleMockMode={onToggleMockMode}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
      />

      {/* Mobile nav toggle bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-2 bg-[var(--bg-surface)] border-b border-[var(--border-default)]">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] px-2.5 py-1.5 rounded bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] active:scale-95 transition-all duration-150"
        >
          {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          <span>MENU: {activeTab.toUpperCase()}</span>
        </button>
        <span className="text-[11px] font-mono text-[var(--text-muted)]">
          {openPositionsCount || 0} POSITIONS
        </span>
      </div>

      {/* Main workspace body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Sidebar */}
        <Sidebar
          activeTab={activeTab}
          onSelectTab={handleSelectTab}
          openPositionsCount={openPositionsCount}
          unacknowledgedSignalsCount={unacknowledgedSignalsCount}
          className="hidden md:flex"
        />

        {/* Mobile Slide-out Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div
              className="fixed inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="relative z-50 w-72 bg-[var(--bg-surface)] h-full shadow-2xl flex flex-col animate-fade-in-up border-r border-[var(--border-default)]">
              <div className="p-4 border-b border-[var(--border-default)] flex items-center justify-between">
                <span className="font-mono font-bold text-sm text-[var(--text-primary)]">MENU</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] active:scale-95 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <Sidebar
                activeTab={activeTab}
                onSelectTab={handleSelectTab}
                openPositionsCount={openPositionsCount}
                unacknowledgedSignalsCount={unacknowledgedSignalsCount}
                className="w-full flex-1 border-r-0"
              />
            </div>
          </div>
        )}

        {/* Dynamic Feature Content Viewport */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 bg-[var(--bg-app)]">
          <div className="max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Bottom Footer */}
      <Footer
        lastSyncTime={lastSyncTime}
        uptime={uptime}
      />

      {/* PWA Install Banner */}
      <PwaInstallBanner />
    </div>
  );
};
